import type { APIInstance } from "./apiService";
import {
  type CaseDetailsResponse,
  type CaseListItem,
  type ChronologyRunResponse,
  ChronologyRunResponseSchema,
  type CreateCaseResponse,
  type MarkCaseReviewedResponse,
  type UpdateCasePhysicianResponse,
} from "./caseServiceV2.types";

export interface GetAllCasesParams {
  firm_id: string;
}

export interface CreateCaseFilePayload {
  filename: string;
  bucket_url: string;
  cloudfront_url: string;
  file_size: number;
  mime_type: string;
  existing_file_id?: string | null;
}

export interface CaseFileResponse {
  file_id: string;
  case_id: string;
  filename: string;
  bucket_url: string;
  cloudfront_url: string;
  category_id: number;
  processing_status: number;
}

export class CaseServiceV2 {
  private api: APIInstance;
  constructor(api: APIInstance) {
    this.api = api;
  }

  async getAllCases(params: GetAllCasesParams): Promise<CaseListItem[]> {
    const { firm_id } = params;

    if (!firm_id) {
      throw new Error("firm_id is required to fetch cases.");
    }

    const response = await this.api.get<CaseListItem[]>(`/firms/${firm_id}/cases`);
    return response.data;
  }

  async getAllAssignedCases(): Promise<CaseListItem[]> {
    const response = await this.api.get<CaseListItem[]>(`/cases`);
    return response.data;
  }

  async getCaseDetails(caseId: string): Promise<CaseDetailsResponse> {
    const response = await this.api.get<CaseDetailsResponse>(`/cases/${caseId}/details`);
    return response.data;
  }

  async createCase(firmId: string, caseName?: string): Promise<CreateCaseResponse> {
    const body = caseName ? { case_name: caseName } : {};
    const response = await this.api.post<CreateCaseResponse>(`/firms/${firmId}/cases`, body);
    return response.data;
  }

  async deleteFileEmbeddedImages(caseId: string, fileId: string): Promise<void> {
    await this.api.delete(`/cases/${caseId}/files/${fileId}/embedded-images`);
  }

  async renameCase(caseId: string, caseName: string): Promise<void> {
    await this.api.patch(`/cases/${caseId}/name`, { case_name: caseName });
  }

  async deleteCase(caseId: string): Promise<void> {
    await this.api.delete(`/cases/${caseId}`);
  }

  async updateCaseStatus(caseId: string, statusId: number): Promise<void> {
    await this.api.put(`/cases/${caseId}/status`, { status_id: statusId });
  }

  async createCaseFile(caseId: string, payload: CreateCaseFilePayload): Promise<CaseFileResponse> {
    const response = await this.api.post<CaseFileResponse>(`/cases/${caseId}/files`, payload);
    return response.data;
  }

  /** The final export uses the case physician's saved options, resolved server-side. */
  async markCaseReviewed(caseId: string): Promise<MarkCaseReviewedResponse> {
    const response = await this.api.post<MarkCaseReviewedResponse>(
      `/cases/${caseId}/status/finished`,
      {}
    );
    return response.data;
  }

  async requestChronologyRun(caseId: string, urgent = false): Promise<ChronologyRunResponse> {
    const response = await this.api.post(`/cases/${caseId}/chronology-runs`, { urgent });
    return ChronologyRunResponseSchema.parse(response.data);
  }

  async updateCasePhysician(caseId: string, userId: string): Promise<UpdateCasePhysicianResponse> {
    const response = await this.api.put<UpdateCasePhysicianResponse>(`/cases/${caseId}/physician`, {
      user_id: userId,
    });
    return response.data;
  }
}
